// userStore.ts
import {defineStore} from 'pinia';
import {UserInfo} from '../framework2-sdk/f2Api';
import config from '../config';
import {
    fetchUserByEmail,
    FullUser, UserFilterKey,
    fetchUsers, fetchFullUser,
    disconnectUserFromCompany, connectUserToCompany, recordCompanyHistory,
    deleteInvitation, updateInvitationStatus,
    updateUserFields, UpdateUserFields,

} from '@/api/user';

export interface User {
    ID: string;
    F2_ID: number;
    name: string;
    surname: string;
    email: string;
}

export interface UserState {
    user: null | UserInfo;
}

export interface Credentials {
    username: string;
    password: string;
    applicationKey?: string;
}

// --- existing auth/session store (unchanged behaviour) ---
export const useUserStore = defineStore({
    id: 'user',
    state: () => ({
        user: null,
    } as UserState),
    actions: {
        initUser(userInfo: UserInfo) {
            this.user = userInfo;
        },
        async checkUserExistsByEmail(email: string): Promise<boolean> {
            const fetchedUser = await fetchUserByEmail(email);
            return !!fetchedUser;
        },
    },
    getters: {
        isAdminUser(state) {
            return state.user?.roleSettings[0]?.uid === config.adminRoleUid;
        },
        isContentManagerUser(state) {
            return state.user?.roleSettings[0]?.uid === config.contentManagerRoleUid;
        },
    },
});

// --- admin tool: walk users, manage company connections + invitations ---
export interface UserAdminState {
    results: FullUser[];
    selected: FullUser | null;
    loading: boolean;
    filterKey: UserFilterKey;
    searchEmail: string;
}

export const useUserAdminStore = defineStore({
    id: 'userAdmin',
    state: () => ({
        results: [],
        selected: null,
        loading: false,
        filterKey: 'all',
        searchEmail: '',
    } as UserAdminState),
    actions: {
        // Loads the full list using current filterKey + searchEmail.
        async load() {
            this.loading = true;
            try {
                this.results = await fetchUsers(this.filterKey, this.searchEmail || undefined);
            } finally {
                this.loading = false;
            }
        },
        async setFilter(key: UserFilterKey) {
            this.filterKey = key;
            await this.load();
        },
        async search(email: string) {
            this.searchEmail = email;
            await this.load();
        },
        async select(F2_ID: number) {
            this.loading = true;
            try {
                this.selected = await fetchFullUser(F2_ID);
            } finally {
                this.loading = false;
            }
        },
        async reload() {
            if (this.selected) await this.select(this.selected.F2_ID);
        },
        // Move = close old history + disconnect old + connect new.
        async moveToCompany(companyUid: string, type: 'Supplier' | 'Customer', admin: boolean) {
            if (!this.selected) return;
            const u = this.selected;
            const old = u.currentCompany;
            const now = new Date().toISOString();
            if (old) {
                const startedAt = u.currentCompanyConnection?.edges?.[0]?.startDate || now;
                if (!(await recordCompanyHistory(u.F2_ID, old.uid, startedAt, now))) {
                    throw new Error('Registrazione storico fallita');
                }
                if (!(await disconnectUserFromCompany(u.F2_ID, old.uid))) {
                    throw new Error('Disconnessione fallita');
                }
            }
            if (!(await connectUserToCompany(u.F2_ID, companyUid, type, admin))) {
                throw new Error('Connessione alla nuova azienda fallita');
            }
            await this.reload();
        },
        async detach() {
            const company = this.selected?.currentCompany;
            if (!this.selected || !company) return;
            const u = this.selected;
            const now = new Date().toISOString();
            const startedAt = u.currentCompanyConnection?.edges?.[0]?.startDate || now;
            if (!(await recordCompanyHistory(u.F2_ID, company.uid, startedAt, now))) {
                throw new Error('Registrazione storico fallita');
            }
            if (!(await disconnectUserFromCompany(u.F2_ID, company.uid))) {
                throw new Error('Disconnessione fallita');
            }
            await this.reload();
        },
        async removeInvitation(ID: string) {
            if (!(await deleteInvitation(ID))) throw new Error('Eliminazione invito fallita');
            await this.reload();
        },
        async resendInvitation(ID: string) {
            const exp = new Date(Date.now() + 7 * 864e5).toISOString();
            if (!(await updateInvitationStatus(ID, exp))) throw new Error('Reinvio fallito');
            await this.reload();
        },
        async saveUserFields(update: UpdateUserFields) {
            if (!this.selected) return;
            if (!(await updateUserFields(this.selected.F2_ID, update))) {
                throw new Error('Salvataggio dati utente fallito');
            }
            await this.reload();
        },
    },
});
